import WDModelItems from './wd-model-items';

export default class WDModelSortItems extends WDModelItems
{
	#sortField;
	#sortDir;

	constructor(cfg)
	{
		super(cfg);

		if (cfg?.sortField) this.#sortField = cfg.sortField;
		if (cfg?.sortDir) this.#sortDir = cfg.sortDir;
	};

	connectedCallback()
	{
		if (this.fetch)
		{
			if (this.sortField) this.fetch.sort_field = this.sortField;
			if (this.sortDir) this.fetch.sort_dir = this.sortDir;
		}
		else if (this.model.length > 0)
		{
			//sort the model using batch operation for performance
			this.#performSort();
		}

		super.connectedCallback();
	};

	get sortField() { return this.#sortField; };
	get sortDir() { return this.#sortDir; };
	set sortField(sf) { this.#sortField = sf; };
	set sortDir(sd) { this.#sortDir = sd; };
	
	setSort(sortField, sortDir)
	{
		// Update sort field and direction
		this.#updateSortParams(sortField, sortDir);

		//we have a fetch, update the sort params and re-fetch
		if (this.fetch)
		{
			this.fetch.sort_field = this.#sortField;
			this.fetch.sort_dir = this.#sortDir;
			this.fetchModel();
		}
		//no fetch, sort the data internally using batch operation
		else if (this.model.length > 0)
		{
			this.#performSort();
		}

		this.trigger('sort:updated', {
			sortField: this.#sortField,
			sortDir: this.#sortDir
		});
	};

	
	#guessDataType()
	{
		// Return 'string' if no model data or sort field
		if (!this.model || this.model.length === 0 || !this.#sortField) {
			return 'string';
		}

		// Find the first item that has the sort field property
		const sampleItem = this.model.find(item => 
			item && typeof item === 'object' && this.#sortField in item
		);

		if (!sampleItem) {
			return 'string';
		}

		const value = sampleItem[this.#sortField];

		// Handle null/undefined values
		if (value === null || value === undefined) {
			return 'string';
		}

		// Check if it's already a number
		if (typeof value === 'number' && !isNaN(value)) {
			return 'number';
		}

		// Check if it's a date (treat dates as separate type for sorting)
		if (value instanceof Date || 
			(typeof value === 'string' && !isNaN(Date.parse(value)))) {
			return 'date';
		}

		// Check if it's a string that represents a number
		if (typeof value === 'string') {
			// Remove common formatting (commas, currency symbols, etc.)
			const cleanValue = value.replace(/[$,\s]/g, '');
			if (!isNaN(cleanValue) && !isNaN(parseFloat(cleanValue))) {
				return 'number';
			}
		}

		// Default to string for everything else
		return 'string';
	};

	#updateSortParams(sortField, sortDir)
	{
		if (sortField == this.#sortField)
		{
			this.#sortDir = (this.#sortDir == 'asc') ? 'desc' : 'asc';
		}
		else
		{
			this.#sortField = sortField;
			this.#sortDir = sortDir;
		}
	}

	#performSort()
	{
		const dataType = this.#guessDataType();
		const isAscending = this.#sortDir === 'asc';

		// Use batch operation for performance when sorting
		this.batchUpdate((model) => {
			switch(dataType) {
				case 'number':
					this.#sortNumber(model, isAscending);
					break;
				case 'date':
					this.#sortDate(model, isAscending);
					break;
				default:
					this.#sortString(model, isAscending);
			}
			return model.length;
		});

		// Trigger sort completed event after batch is done
		this.trigger('sort:completed', {
			sortField: this.#sortField,
			sortDir: this.#sortDir,
			dataType: dataType,
			itemCount: this.model.length
		});
	}

	#sortNumber(model, ascending = true)
	{
		// Get sorted array
		const sortedData = model.toArray().sort((a, b) =>
		{
			const valA = parseFloat(a[this.#sortField]) || 0;
			const valB = parseFloat(b[this.#sortField]) || 0;
			const diff = valA - valB;
			return ascending ? diff : -diff;
		});

		// Replace model contents with sorted data
		model.replace(sortedData);
	}

	#sortDate(model, ascending = true)
	{
		// Get sorted array
		const sortedData = model.toArray().sort((a, b) =>
		{
			const dateA = new Date(a[this.#sortField]);
			const dateB = new Date(b[this.#sortField]);
			const diff = dateA.getTime() - dateB.getTime();
			return ascending ? diff : -diff;
		});

		// Replace model contents with sorted data
		model.replace(sortedData);
	}

	#sortString(model, ascending = true)
	{
		// Get sorted array
		const sortedData = model.toArray().sort((a, b) =>
		{
			const valA = String(a[this.#sortField] || '');
			const valB = String(b[this.#sortField] || '');
			const result = valA.localeCompare(valB);
			return ascending ? result : -result;
		});

		// Replace model contents with sorted data
		model.replace(sortedData);
	}

	/**
	 * Toggle sort on a field (switches between asc/desc/none)
	 */
	toggleSort(sortField)
	{
		if (this.#sortField === sortField) {
			if (this.#sortDir === 'asc') {
				this.setSort(sortField, 'desc');
			} else if (this.#sortDir === 'desc') {
				// Third click removes sorting
				this.clearSort();
			}
		} else {
			this.setSort(sortField, 'asc');
		}
	}

	/**
	 * Clear sorting and restore original order (if available)
	 */
	clearSort()
	{
		this.#sortField = null;
		this.#sortDir = null;

		if (this.fetch) {
			delete this.fetch.sort_field;
			delete this.fetch.sort_dir;
			this.fetchModel();
		}

		this.trigger('sort:cleared');
	}

	/**
	 * Get current sort state
	 */
	getSortState()
	{
		return {
			field: this.#sortField,
			direction: this.#sortDir,
			dataType: this.#guessDataType(),
			isSorted: !!this.#sortField
		};
	}

	/**
	 * Apply custom sort function
	 */
	sortCustom(compareFn, sortInfo = {})
	{
		this.batchUpdate((model) => {
			const sortedData = model.toArray().sort(compareFn);
			model.replace(sortedData);
			return model.length;
		});

		this.trigger('sort:custom-completed', {
			sortInfo: sortInfo,
			itemCount: this.model.length
		});
	}

	// Utility methods for type detection
	#isNumeric(value)
	{
		if (typeof value === 'number') return !isNaN(value);
		if (typeof value === 'string') {
			const cleanValue = value.replace(/[$,\s]/g, '');
			return !isNaN(cleanValue) && !isNaN(parseFloat(cleanValue));
		}
		return false;
	}

	#isDate(value)
	{
		if (value instanceof Date) return true;
		if (typeof value === 'string') return !isNaN(Date.parse(value));
		return false;
	}

	// Override handleModelFetchResults to apply sorting if needed
	handleModelFetchResults(results)
	{
		// Call parent method first
		super.handleModelFetchResults(results);
		return;
		// If we have local sorting and the fetch didn't handle it server-side,
		// apply sorting after the data is loaded
		if (this.#sortField && this.model.length > 0 && !this.#wasSortedServerSide()) {
			this.#performSort();
		}
	}

	#wasSortedServerSide()
	{
		// Check if the fetch object has sort parameters that would indicate
		// server-side sorting was performed
		return this.fetch && 
			   (this.fetch.sort_field === this.#sortField) &&
			   (this.fetch.sort_dir === this.#sortDir);
	}

	// Enhanced batch event handler to preserve sort order
	modelBatchCompleted(evtData)
	{
		// Call parent handler first
		super.modelBatchCompleted(evtData);
		return;
		// If we had sorting applied and items were added/changed, re-sort
		if (this.#sortField && (evtData.changes.length > 0)) {
			// Small delay to allow DOM updates to complete
			setTimeout(() => {
				this.#performSort();
			}, 10);
		}
	}

	// Convenience method to sort items and get result
	sortItems(compareFn)
	{
		// Override parent method to use batch operations
		this.batchUpdate((model) => {
			const sortedData = model.toArray().sort(compareFn);
			model.replace(sortedData);
			return model.length;
		});
		
		return this;
	}
};

customElements.define('wd-model-sort-items', WDModelSortItems);
